import { useDispatch, useSelector } from 'react-redux';

import { CheckboxGroupQuestion } from 'src/lib/flow/FlowQuestion';
import { getAnswers } from 'src/store/sellerFlow/selectors';
import { changeAnswers, SavedAnswer } from 'src/store/sellerInput';

import { noneKey } from '../../helpers/questions';
import { CheckboxGroup as BricksCheckBoxGroup } from '../common/CheckboxGroup';
import QuestionLabel from '../common/QuestionLabel';
import Fieldset from './Fieldset';
import { getOptions } from './getOptions';
import { getAllKeys } from './RadioGroup';
import { trackAnswerChange } from './trackAnswerChange';

const getKeysToClear = (
  options: CheckboxGroupQuestion['options'],
  newValues: Record<string, boolean>,
) => {
  const keys: Array<string> = [];
  (options ?? []).forEach((option) => {
    if (!(typeof option.value === 'string' && newValues[option.value]) && option.followUps) {
      keys.push(...getAllKeys(option.followUps));
    }
  });
  return keys;
};

type CheckboxGroupProps = {
  question: CheckboxGroupQuestion;

  fieldsetLegend?: string;
  isFollowUp: boolean;
  hasError: boolean;
  pageHasError: boolean;
  values?: Record<string, boolean>;
};

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  question,
  values,
  fieldsetLegend,
  isFollowUp,
  hasError,
  pageHasError,
}) => {
  const dispatch = useDispatch();
  const answers = useSelector(getAnswers);
  const { label, helperText, options = [], key, showNoneOfTheAbove } = question;

  const onClick = (value: string) => {
    const name = question.key;
    const answer = values;
    const newSelections = answer ? { ...answer } : {};

    const isChecked = !newSelections[value];

    if (value !== noneKey) {
      // user did not click the none option, so make sure it is set to false
      newSelections[noneKey] = false;
    } else if (isChecked) {
      // user clicked and enabled the none option, so clear out existing values
      Object.entries(newSelections).forEach(([key, _]) => {
        newSelections[key] = false;
      });
    }

    // update our clicked item to the new checked state
    newSelections[value] = isChecked;

    const newAnswers: Array<{ key: string; answer: SavedAnswer | null }> = [];

    if (value === '_other_' && isChecked) {
      newAnswers.push({ key: `${name}.other`, answer: '_other_' });
    }

    // clear out any followups that are unselected
    const keys = getKeysToClear(options, newSelections);
    keys.forEach((key) => {
      newAnswers.push({ key, answer: null });
    });

    newAnswers.push({ key: name, answer: newSelections });
    dispatch(changeAnswers(newAnswers));
    trackAnswerChange(name, newSelections);
  };

  const getSelections = (answer?: Record<string, boolean>) => {
    if (!answer) {
      return [];
    }
    return Object.entries(answer)
      .filter(([_, value]) => value === true)
      .map(([key, _]) => key);
  };

  let legendText = fieldsetLegend || '';
  if (isFollowUp) {
    // This prevents follow up checkbox groups from having both <fieldset> and <div role="group">
    // since role="group" is done in bricks we make it so that we do not render the <fieldset>
    legendText = '';
  }

  return (
    <>
      <QuestionLabel id={`${key}-label`} title={label} tooltip={helperText} unboundedWidth />
      <Fieldset questionTitle={legendText}>
        <span style={{ display: 'block' }}>{false}</span>
        <BricksCheckBoxGroup
          hasContainer={!isFollowUp}
          id={key}
          ariaLabelledBy={legendText == '' ? `${key}-label` : undefined}
          noneOfTheAboveValue={noneKey}
          onClick={onClick}
          selectedValues={getSelections(values)}
          options={getOptions(
            options,
            answers,
            onClick, //TODO ensure yea
            !!hasError,
            !!pageHasError,
            !!isFollowUp,
            getSelections(values),
            showNoneOfTheAbove,
          )}
        />
      </Fieldset>
    </>
  );
};

export default CheckboxGroup;
